// src/modules/Rules/components/modals/StockModal.tsx
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { faBox } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Product as ProductInfo } from '@shared/components/ui/Product';

export interface ProductStockModalData {
  name: string;
  code: string;
  image?: string;
  stock: {
    disponible: number;
    reservado: number;
    total: number;
  };
}

export interface StockModalRef {
  open: (product: ProductStockModalData) => void;
}

export const StockModal = forwardRef<StockModalRef>((_, ref) => {
  const { t } = useTranslation('productsModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const [product, setProduct] = useState<ProductStockModalData | null>(null);

  useImperativeHandle(ref, () => ({
    open: (productData: ProductStockModalData) => {
      setProduct(productData);
      modalRef.current?.openModal();
    },
  }));

  return (
    <ModalCenter
      ref={modalRef}
      title={t('stock_breakdown')}
      icon={<FontAwesomeIcon icon={faBox} className="w-3 h-3" />}
      type="default"
      width="lg"
      className="h-[100dvh] sm:h-auto w-full sm:w-[579px]"
    >
      {product && (
        <div className="space-y-4 pt-3 pb-2">
          <ProductInfo
            id={0}
            name={product.name}
            src={product.image || ''}
            code={product.code}
            showImageModal={false}
          />
          <div className="flex gap-4">
            <div className="flex-1 bg-card-bg rounded p-2 text-center border border-card-border">
              <div className="text-sm">{t('table.available')}</div>
              <div className="text-base font-semibold">{product.stock.disponible}</div>
            </div>
            <div className="flex-1 bg-card-bg rounded p-2 text-center border border-card-border">
              <div className="text-sm">{t('table.reserved')}</div>
              <div className="text-base font-semibold">{product.stock.reservado}</div>
            </div>
            <div className="flex-1 bg-card-bg rounded p-2 text-center border border-card-border">
              <div className="text-sm">{t('table.stock')}</div>
              <div className="text-base font-semibold">{product.stock.total}</div>
            </div>
          </div>
        </div>
      )}
    </ModalCenter>
  );
});

StockModal.displayName = 'StockModal';
